<template>
  <CCard class="settings-card">
    <CCardHeader>
      <div class="section-heading">
        <span class="section-icon"><CIcon name="cil-fingerprint"/></span>
        <div>
          <strong>パスキー</strong>
          <p>
            この端末の指紋・顔・PINでログインします。
            パスワードを入力しないので、盗まれる文字列がありません。
          </p>
        </div>
      </div>
    </CCardHeader>

    <CCardBody>
      <div v-if="loading" class="security-settings-loading">
        <CSpinner color="primary"/>
        <span>登録済みのパスキーを読み込んでいます。</span>
      </div>

      <template v-else>
        <CAlert
          v-if="errorMessage"
          color="danger"
          close-button
          @update:show="errorMessage = ''"
        >{{ errorMessage }}</CAlert>

        <CAlert
          v-if="message"
          color="success"
          close-button
          @update:show="message = ''"
        >{{ message }}</CAlert>

        <!--
          登録はできるがログインには使えない、という状態がありうる。
          **黙って登録させない。** 登録したのに使えないと、利用者は
          自分の操作が失敗したと思う。
        -->
        <CAlert v-if="credentials.length && !loginAvailable" color="warning">
          パスキーは登録できていますが、<b>いまはログインに使えません</b>。
          {{ productName }}でパスキーのログインを開けていないためです。
          管理者へお問い合わせください。
        </CAlert>

        <p v-if="!supported" class="security-empty">
          この端末（またはブラウザ）はパスキーに対応していません。
          Windows Hello、Touch ID、スマートフォンの生体認証などが使える
          端末でお試しください。
        </p>

        <template v-else>
          <p v-if="!credentials.length" class="security-empty">
            登録されたパスキーはありません。
          </p>

          <ul v-else class="security-session-list">
            <li
              v-for="item in credentials"
              :key="item.id"
              class="security-session"
            >
              <div class="security-session__body">
                <p class="security-session__title">
                  {{ item.label || '名前のないパスキー' }}
                </p>
                <p class="security-session__detail">
                  登録した日時: {{ formatTime(item.createdAt) }}
                </p>
                <p class="security-session__detail">
                  最後に使ったの: {{ formatTime(item.lastUsedAt) }}
                </p>
              </div>
              <CButton
                color="outline-danger"
                size="sm"
                :disabled="busy"
                @click="remove(item)"
              >削除</CButton>
            </li>
          </ul>

          <div class="passkey-register">
            <CInput
              v-model="label"
              type="text"
              maxlength="60"
              placeholder="この端末の名前（例: 会社のノートPC）"
              :disabled="busy"
            />
            <CButton
              color="primary"
              :disabled="busy"
              @click="register"
            >この端末を登録する</CButton>
          </div>
          <p class="security-settings-note">
            登録すると、この端末の画面ロック（指紋・顔・PIN）で
            ログインできるようになります。
          </p>
        </template>
      </template>
    </CCardBody>
  </CCard>
</template>

<script>
import {
  isPasskeySupported,
  isPasskeyLoginAvailable,
  registerPasskey,
  listPasskeys,
  deletePasskey,
  isReauthenticationRequired,
  isNetworkFailure
} from '../../services/passkey'
import { isLastSignInMethod } from '../../services/externalIdpLogin'

/**
 * 自分のパスキーを登録する・消す画面。
 *
 * <h3>登録できることと、ログインに使えることは別</h3>
 *
 * <p>登録はPREIKA全体のスイッチと関係なくできる。ログインに使えるのは
 * スイッチが開いているときだけ。<b>その差を黙っていると、登録したのに
 * 使えない理由が分からない。</b>だから登録済みなのに開いていないときは、
 * その場で伝える。</p>
 *
 * <h3>最後の1つは消させない</h3>
 *
 * <p>パスワードを使わない設定にしている人が、最後のパスキーを消すと
 * 入れなくなる。サーバが断るので、画面はその理由をそのまま出す。</p>
 */
import { PRODUCT_NAME } from '../../common/productName'

export default {
  name: 'PasskeySettings',

  computed: {
    /** 売るときの正式な表記。**1か所から取る。** */
    productName () {
      return PRODUCT_NAME
    }
  },

  data () {
    return {
      supported: false,
      loginAvailable: false,
      credentials: [],
      label: '',
      loading: true,
      busy: false,
      message: '',
      errorMessage: ''
    }
  },

  mounted () {
    this.supported = isPasskeySupported()
    this.reload()
  },

  methods: {
    async reload () {
      this.loading = true
      try {
        /*
         * 一覧が読めないことと、スイッチが閉じていることは別。
         * **一覧の失敗だけをエラーにする。**スイッチは聞けなければ
         * 閉じている扱いで、それ自体は異常ではない。
         */
        this.loginAvailable = await isPasskeyLoginAvailable()
      } catch (error) {
        this.loginAvailable = false
      }

      try {
        this.credentials = await listPasskeys()
      } catch (error) {
        this.credentials = []
        this.errorMessage = this.explain(error,
          '登録済みのパスキーを読み込めませんでした。')
      } finally {
        this.loading = false
      }
    },

    async register () {
      if (this.busy) return
      this.busy = true
      this.message = ''
      this.errorMessage = ''
      try {
        await registerPasskey(this.label.trim())
        this.label = ''
        this.message = 'この端末をパスキーとして登録しました。'
        await this.reload()
      } catch (error) {
        this.errorMessage = this.explainRegister(error)
      } finally {
        this.busy = false
      }
    },

    async remove (item) {
      if (this.busy) return
      this.busy = true
      this.message = ''
      this.errorMessage = ''
      try {
        await deletePasskey(item.id)
        this.message = 'パスキーを削除しました。'
        await this.reload()
      } catch (error) {
        this.errorMessage = this.explain(error, 'パスキーを削除できませんでした。')
      } finally {
        this.busy = false
      }
    },

    /**
     * 登録が通らなかったときの見せ方。
     *
     * <p>ブラウザ側で断られる場合（利用者が取り消した、端末が対応して
     * いない、ドメインが合っていない）は、サーバの応答が無い。
     * <b>通信の失敗と混ぜない。</b>混ぜると「接続を確認してください」と
     * 出て、実際には接続の問題ではない。</p>
     */
    explainRegister (error) {
      if (isReauthenticationRequired(error)) {
        return 'もう一度ログインの確認が必要です。'
          + ' 一度ログアウトして入り直してから、もう一度お試しください。'
      }
      if (error && error.name === 'NotAllowedError') {
        return '登録が取り消されました。もう一度お試しください。'
      }
      if (error && error.name === 'SecurityError') {
        return 'この画面のURLではパスキーを登録できません。'
          + ' 管理者へお問い合わせください（設定が必要です）。'
      }
      if (error && error.name === 'InvalidStateError') {
        return 'この端末は、すでにパスキーとして登録されています。'
      }
      if (isNetworkFailure(error)) {
        return '通信できませんでした。接続を確認してからお試しください。'
      }
      return this.serverText(error) || 'パスキーを登録できませんでした。'
    },

    explain (error, fallback) {
      if (isReauthenticationRequired(error)) {
        return 'もう一度ログインの確認が必要です。'
          + ' 一度ログアウトして入り直してから、もう一度お試しください。'
      }
      if (isLastSignInMethod(error)) {
        return this.serverText(error)
          || 'これが最後のログイン方法なので、削除できません。'
          + ' 先に別のログイン方法を用意してください。'
      }
      if (isNetworkFailure(error)) {
        return '通信できませんでした。接続を確認してからお試しください。'
      }
      return this.serverText(error) || fallback
    },

    serverText (error) {
      const response = error && error.response
      const data = (response && response.data) || {}
      return typeof data.message === 'string' && data.message
        ? data.message
        : null
    },

    formatTime (value) {
      if (!value) return '未使用'
      const at = new Date(value)
      if (isNaN(at.getTime())) return '不明'
      return at.toLocaleString('ja-JP')
    }
  }
}
</script>
